// The PDF beside the quote document review (mock M7): every page of the uploaded file drawn
// to the pane's width, and one printed line lit up on its page when someone asks where it is.
// Drawing is pdf.js in a lazy chunk (pdf-engine.ts), reached through PdfOpener so a test can
// hand the app a fake that draws nothing.
import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Icon } from "./icons.js";
import { useSession } from "./session.js";
import { errorText, plural } from "./format.js";
import { fileSize } from "./api-intakes.js";

/** A run of printed text as drawn, in CSS pixels from the page's top left corner. */
export interface PdfRun {
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PdfPageDrawing {
  width: number;
  height: number;
  runs: PdfRun[];
}

export interface PdfDoc {
  pages: number;
  /** Draws page `n` (from 1) onto the canvas at `cssWidth` CSS pixels wide, and says where its text landed. */
  draw(n: number, canvas: HTMLCanvasElement, cssWidth: number): Promise<PdfPageDrawing>;
  destroy(): void;
}

export type PdfOpener = (data: Uint8Array) => Promise<PdfDoc>;

async function openWithPdfJs(data: Uint8Array): Promise<PdfDoc> {
  // Dynamic on purpose: esbuild splits pdf.js into its own chunk, so only the review downloads it.
  const { openPdf } = await import("./pdf-engine.js");
  return openPdf(data);
}

const PdfContext = createContext<PdfOpener>(openWithPdfJs);
export const PdfProvider = PdfContext.Provider;

/** A line to light up: its page and the words it was printed with (its description, code and full text). */
export interface PdfHighlight {
  page: number;
  keys: string[];
  /** Bumped on every ask, so asking for the same line again scrolls to it again. */
  seq: number;
}

const norm = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9$.,/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Where a printed line sits on its page: the run sharing the most text with the line's words, widened to every run on
 * the same row. null when nothing on the page matches, so the pane shows the page without guessing a row.
 */
export function findBand(runs: PdfRun[], keys: string[]): { top: number; height: number } | null {
  const wanted = keys.map(norm).filter((k) => k.length >= 3);
  let best: PdfRun | null = null;
  let bestScore = 0;
  for (const r of runs) {
    const t = norm(r.text);
    if (t.length < 3) continue;
    for (const k of wanted) {
      const score = k.includes(t) ? t.length : t.includes(k) ? k.length : 0;
      if (score > bestScore) {
        best = r;
        bestScore = score;
      }
    }
  }
  if (!best) return null;
  const mid = best.y + best.h / 2;
  const row = runs.filter((r) => Math.abs(r.y + r.h / 2 - mid) <= Math.max(best.h, r.h) * 0.6);
  const top = Math.min(...row.map((r) => r.y));
  const bottom = Math.max(...row.map((r) => r.y + r.h));
  const pad = best.h * 0.45;
  return { top: top - pad, height: bottom - top + 2 * pad };
}

/** Moves the lit row to the middle of whatever scrolls (the pane on a wide screen, the page on a phone), or a whole page to its top. */
function scrollIntoView(el: HTMLElement, pane: HTMLElement | null, middle: boolean): void {
  if (pane && getComputedStyle(pane).overflowY === "auto" && pane.scrollHeight > pane.clientHeight) {
    pane.scrollTop += el.getBoundingClientRect().top - pane.getBoundingClientRect().top - (middle ? pane.clientHeight / 2 : 14);
  } else {
    window.scrollBy(0, el.getBoundingClientRect().top - (middle ? window.innerHeight / 2 : 70));
  }
}

function PdfPage({
  doc,
  n,
  width,
  highlight,
  scanning,
  pane,
}: {
  doc: PdfDoc;
  n: number;
  width: number;
  highlight: PdfHighlight | null;
  scanning: boolean;
  pane: React.RefObject<HTMLDivElement | null>;
}): React.ReactElement {
  const canvas = useRef<HTMLCanvasElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const band = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState<PdfPageDrawing | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!canvas.current || width <= 0) return;
    let live = true;
    doc.draw(n, canvas.current, width).then(
      (d) => {
        if (live) setDrawn(d);
      },
      (err: unknown) => {
        // A redraw cancels the one before it; only a real failure is shown.
        if (live && !(err instanceof Error && err.name === "RenderingCancelledException")) setProblem(errorText(err));
      },
    );
    return () => {
      live = false;
    };
  }, [doc, n, width]);

  const lit = highlight && highlight.page === n && drawn ? findBand(drawn.runs, highlight.keys) : null;
  // A scan has no text to find the row in, so the page itself comes into view.
  const seq = highlight && highlight.page === n && drawn ? highlight.seq : null;
  useEffect(() => {
    if (seq == null) return;
    if (band.current) scrollIntoView(band.current, pane.current, true);
    else if (sheet.current) scrollIntoView(sheet.current, pane.current, false);
  }, [seq, pane]);

  return (
    <div className="sheetwrap">
      <div className={drawn ? "page pdf" : "page pdf blank"} ref={sheet} data-page={n} data-testid={`pdf-page-${n}`}>
        <canvas ref={canvas} aria-label={`Page ${n}`} />
        {lit ? <div className="hlband" ref={band} data-testid="pdf-highlight" style={{ top: lit.top, height: lit.height }} /> : null}
        {problem ? <p className="pdf-problem">Page {n} didn&rsquo;t draw: {problem}</p> : null}
      </div>
      {scanning ? <div className="scanline" /> : null}
    </div>
  );
}

/**
 * The pane: the file's name, its pages and size, and on a phone a button that shows or hides it. `mode` "reading" runs
 * the scan line over each page while Omni reads it.
 */
export function PdfPane({
  intakeId,
  partIndex = 0,
  filename,
  bytes,
  mode,
  highlight,
  open,
  onToggle,
  onPages,
}: {
  intakeId: string;
  partIndex?: number;
  filename: string;
  bytes: number;
  mode: "reading" | "review" | "fail" | "done";
  highlight: PdfHighlight | null;
  open: boolean;
  onToggle: () => void;
  onPages?: (pages: number) => void;
}): React.ReactElement {
  const { api } = useSession();
  const opener = useContext(PdfContext);
  const pane = useRef<HTMLDivElement>(null);
  const [doc, setDoc] = useState<PdfDoc | null>(null);
  const [href, setHref] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    let live = true;
    let opened: PdfDoc | null = null;
    let url: string | null = null;
    (async () => {
      const blob = await api.quoteIntakePart(intakeId, partIndex);
      if (!live) return;
      if (typeof URL.createObjectURL === "function") {
        url = URL.createObjectURL(blob);
        setHref(url);
      }
      const d = await opener(new Uint8Array(await blob.arrayBuffer()));
      if (!live) {
        d.destroy();
        return;
      }
      opened = d;
      setDoc(d);
    })().catch((err: unknown) => {
      if (live) setProblem(errorText(err));
    });
    return () => {
      live = false;
      opened?.destroy();
      if (url) URL.revokeObjectURL(url);
    };
  }, [api, opener, intakeId, partIndex]);

  const pages = doc?.pages ?? null;
  useEffect(() => {
    if (pages != null) onPages?.(pages);
  }, [pages, onPages]);

  // Draw to the pane's width, and again when it changes (a resize, or the phone's Show the PDF).
  useLayoutEffect(() => {
    const el = pane.current;
    if (!el) return;
    const measure = () => {
      const w = Math.floor(el.clientWidth - 28);
      setWidth((before) => (Math.abs(before - w) >= 2 ? w : before));
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    return () => watch.disconnect();
  }, [open]);

  return (
    <aside className={`ix-pdf${open ? " open" : ""}${mode === "reading" ? " reading" : ""}`} aria-label="The PDF" data-testid="pdf-pane">
      <div className="pbar">
        <Icon name="file-text" size={14} />
        <b title={filename}>{filename}</b>
        <span data-testid="pdf-facts">{[pages != null ? plural(pages, "page") : null, fileSize(bytes)].filter(Boolean).join(" \u00b7 ")}</span>
        <button className="btn small ptoggle" type="button" aria-expanded={open} data-testid="pdf-toggle" onClick={onToggle}>
          <Icon name={open ? "x" : "eye"} size={14} />
          {open ? "Hide the PDF" : "Show the PDF"}
        </button>
      </div>
      <div className="pscroll" ref={pane} role="region" aria-label="PDF pages" tabIndex={0}>
        {problem ? (
          <div className="pdf-problem" data-testid="pdf-problem">
            Omni couldn&rsquo;t show the PDF here: {problem}
            {href ? (
              <>
                {" "}
                <a href={href} target="_blank" rel="noreferrer">
                  Open the PDF
                </a>
              </>
            ) : null}
          </div>
        ) : !doc ? (
          <div className="sheetwrap">
            <div className="page pdf blank" />
            {mode === "reading" ? <div className="scanline" /> : null}
          </div>
        ) : (
          Array.from({ length: doc.pages }, (_, k) => (
            <PdfPage key={k + 1} doc={doc} n={k + 1} width={width} highlight={highlight} scanning={mode === "reading"} pane={pane} />
          ))
        )}
      </div>
    </aside>
  );
}
